/* eslint-disable no-console */

"use client";

import React, { useState } from "react";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import { Box, CircularProgress, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useSession } from "next-auth/react";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import VisibilitySharp from "@mui/icons-material/VisibilitySharp";
import Error from "@mui/icons-material/Error";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import Link from "next/link";
import Image from "next/image";
import useLoginBlock from "@/hooks/login/useLoginBlock";
import isEmail from "@/utils/validation/is-email-util";
import { errorMessageHandler } from "@/utils/login-utils";
import { processGroups, handleGroupRedirection } from "@/utils/user-groups-utils";
import DataLayer from "@/components/data-layer/data-layer";
import { useAuthStatusContext } from "@/providers/AuthStatusProvider";
import styles from "./styles.module.css";

interface LoginFormData {
  username: string;
  password: string;
  auth: string;

}

const LoginForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedError, setSubmittedError] = useState<string | null>(null);
  const { update } = useSession();
  const { checkAuth } = useAuthStatusContext();
  const formProviderProps = useForm<LoginFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      username: "",
      password: ""
    }
  });

  const { handleSubmit, control, formState } = formProviderProps;
  const { data: loginBlock } = useLoginBlock();
  const [showPassword, setShowPassword] = useState(false);
  const getErrorMessage = (error: string): string =>
    errorMessageHandler(error, loginBlock?.errorBoxMessages || []);
  const [dataLayerProps, setDataLayerProps] = useState<Record<string, any> | null>(null);
  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsSubmitting(true);
      setSubmittedError(null); // Clear previous errors

      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        // Display the error message returned by the API
        setSubmittedError(result.error || "An unexpected error occurred");
        setIsSubmitting(false);
        setIsSubmitted(false);
        return;
      }

      const { pivotalId, groups } = result;
      console.log("Pivotal ID:", pivotalId, "Username:", data.username, groups, "Groups:", groups);

      // Set the data layer props if we have enough information
      if (pivotalId && data.username) {
        console.log("Pivotal ID:", pivotalId, "Username:", data.username);
        setDataLayerProps({
          event: "dataLayerPush",
          userName: data.username,
          pivotalId
        });
      }

      // Handle successful login
      setIsSubmitted(true);
      await update();
      await checkAuth();

      // Process user groups and store in context instead of localStorage
      if (result.groups && result.groups.length > 0) {
        // Process groups using our utils function
        const processedGroups = processGroups(result.groups);

        // Handle group-based redirection with our improved function
        handleGroupRedirection(
          processedGroups,
          loginBlock?.travelerRedirect || "",
          loginBlock?.agentRedirect || ""
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      setSubmittedError(String(error));
      setIsSubmitting(false);
      setIsSubmitted(false);
    }
  };

  return (
    <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      {dataLayerProps && <DataLayer data={dataLayerProps} />}
      <Stack
        sx={{
          display: "flex",
          width: {
            xs: "100%",
            sm: "46vw"
          },
          maxWidth: {
            xs: "100%",
            sm: "42.6rem"
          },
          padding: "1rem 3rem",
          flexDirection: "column",
          alignItems: "center",
          gap: "3rem",
          margin: "auto",
          color: "primary.main"
        }}
      >
        <Box>
          {loginBlock?.colletteLogo?.url ? (
            <Image
              src={loginBlock.colletteLogo.url}
              alt={loginBlock.colletteLogo.altText}
              width={150}
              height={50}
              priority
            />
          ) : (
            <Typography variant="h6" />
          )}
        </Box>

        <Typography variant="h5">{loginBlock?.headerText}</Typography>
        {submittedError && (
          <Box sx={{
            display: "flex",
            flexDirection: "row",
            gap: "1rem",
            padding: "1rem",
            backgroundColor: "error.95",
            borderRadius: "1rem",
            color: "error.main",
            border: "1px solid red"
          }}>
            <ErrorOutline color="error" fontSize="large" />
            <Typography variant="body1" className="error" data-testid="login--error">
              {getErrorMessage(submittedError)}

            </Typography>
          </Box>
        )}
        <Controller
          name="username"
          control={control}
          rules={{
            required: loginBlock?.errorBoxMessages?.find(msg => msg.name === "requiredUsername")?.message || "Please provide your email address",
            validate: (value) => isEmail(value) || loginBlock?.errorBoxMessages?.find(msg => msg.name === "validEmail")?.message
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              data-testid="username-field"
              label={loginBlock?.username.label}
              placeholder={loginBlock?.username.placeholder}
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              aria-label="Username"
              size="small"
              className={styles["custom-input"]}
              slotProps={{
                formHelperText: {
                  "data-testid": "username-error-text"
                } as unknown as React.ComponentProps<"p">,
                input: {
                  endAdornment: fieldState.error ? (
                    <InputAdornment position="end">
                      <Error color="error" fontSize="small" />
                    </InputAdornment>
                  ) : null
                }
              }}
              sx={{
                "& .MuiInputLabel-root": { color: "primary.main" },
                "& .MuiOutlinedInput-root": { borderRadius: "1rem" }
              }}
            />
          )}
        />

        <Controller
          name="password"      
          control={control}
          rules={{
            required: loginBlock?.errorBoxMessages?.find(msg => msg.name === "requiredPassword")?.message,
            // if the password has a space character, return the error message
            validate: (value) =>
              !/\s/.test(value) || loginBlock?.errorBoxMessages?.find(msg => msg.name === "unallowedSpace")?.message
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              className={styles["custom-input"]}
              data-testid="password-field"
              label={loginBlock?.password.label}
              placeholder={loginBlock?.password.placeholder}
              type={showPassword ? "text" : "password"}
              fullWidth
              error={!!fieldState.error}
              helperText={fieldState.error?.message} // Displays the error message
              aria-label="Password"
              size="small"
              sx={{
                "& .MuiInputLabel-root": { color: "primary.main" },
                "& .MuiOutlinedInput-root": { borderRadius: "1rem" },
                backgroundColor: fieldState.error ? "" : "background.cool",
                borderRadius: "1rem"
              }}
              slotProps={{
                formHelperText: {
                  "data-testid": "password-error-text"
                } as unknown as React.ComponentProps<"p">,
                input: {
                  endAdornment: fieldState.error ? (
                    <InputAdornment position="end">
                      <Error color="error" fontSize="small" />
                    </InputAdornment>) :
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <VisibilitySharp />}
                      </IconButton>
                    </InputAdornment>

                }
              }}
            />
          )}
        />

        <ColletteButton
          disabled={isSubmitting || !formState.isValid}
          label={(!isSubmitting && !isSubmitted) ? "Log in" : ""}
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          sx={{
            textTransform: "none"
          }}
          startIcon={(isSubmitting || isSubmitted) && (
            <CircularProgress
              size="3.2rem"
              thickness={5}
              title="Loading"
              sx={{ color: "common.white", paddingY: "0.4rem" }}
            />
          )}
         testId="login-form-submit"
        />

        <Typography>
          <Link data-testid="forgot-password-link" href={`${loginBlock?.forgotPassword?.url}`}>{loginBlock?.forgotPassword?.title}</Link>
        </Typography>
        <Typography>{loginBlock?.noAccountText}  <Link data-testid="sign-up-link" href={`${loginBlock?.signUp?.url}`}>{loginBlock?.signUp?.title}</Link></Typography>
      </Stack>
    </Box>
  );
};

export default LoginForm;
